import {
  Box,
  Collapse,
  HStack,
  ListItem,
  Stack,
  StackDivider,
  Text,
  UnorderedList
} from '@chakra-ui/react';
import { IReviewTimeline } from '@publishing-engine/domain/models';
import { formatDeadlineDate } from 'src/utils/date';
import { convertNumberToOrdinal } from 'src/utils/number';

export default function ReviewTimeline({
  isOpen,
  reviewTimelineData
}: {
  isOpen: boolean;
  reviewTimelineData: IReviewTimeline;
}) {
  const formattedDate = (date: Date) => {
    return formatDeadlineDate(new Date(date))?.split(',')[0];
  };

  return (
    <Collapse in={isOpen} animateOpacity style={{ width: '100%' }}>
      <Box
        p="8px"
        color="gray.900"
        w="100%"
        bg="gray.200"
        rounded="md"
        shadow="md"
      >
        <Stack spacing={2} w="100%">
          <HStack
            spacing={2}
            align="center"
            divider={<StackDivider borderColor="gray.900" />}
          >
            <Text as="span" textStyle="textContext">
              Reviewer Invited:{' '}
              {reviewTimelineData.invitedDate
                ? formattedDate(new Date(reviewTimelineData.invitedDate))
                : 'No data available'}
            </Text>
            <Text as="span" textStyle="textContext">
              Review Accepted:{' '}
              {reviewTimelineData.acceptedDate
                ? formattedDate(new Date(reviewTimelineData.acceptedDate))
                : 'No data available'}
            </Text>
          </HStack>
          <HStack spacing={2} divider={<StackDivider borderColor="gray.900" />}>
            <Text as="span" textStyle="textContext">
              Original Due:{' '}
              {reviewTimelineData.originalDueDate
                ? formattedDate(new Date(reviewTimelineData.originalDueDate))
                : 'No data available'}
            </Text>
            {reviewTimelineData.originalOverdue && (
              <Text as="span" fontWeight="medium" color="errorFillInverse">
                Overdue: {reviewTimelineData.originalOverdue} day
                {reviewTimelineData.originalOverdue > 1 ? 's' : ''}
              </Text>
            )}
            <Text as="span">
              Total Extensions: {reviewTimelineData.totalExtensions}
            </Text>
          </HStack>
          {reviewTimelineData.deadlineHistory &&
            reviewTimelineData.deadlineHistory.length > 0 && (
              <UnorderedList>
                {(reviewTimelineData.deadlineHistory ?? []).map(
                  (historyGroup, groupIndex) => (
                    <Box key={groupIndex}>
                      <>
                        {historyGroup.map((extension, index) => (
                          <ListItem key={`${groupIndex}-${index}`}>
                            <HStack
                              spacing={2}
                              divider={<StackDivider borderColor="gray.900" />}
                            >
                              <Text as="span" textStyle="textContext">
                                {convertNumberToOrdinal(index + 1)} Extension
                                Granted:{' '}
                                {formattedDate(new Date(extension.grantedDate))}
                              </Text>

                              <Text as="span" textStyle="textContext">
                                Duration: {extension.deadlineDays} day
                                {extension.deadlineDays > 1 ? 's' : ''}
                              </Text>

                              <Text as="span" textStyle="textContext">
                                New Due:{' '}
                                {formattedDate(new Date(extension.newDeadline))}
                              </Text>

                              {extension.overdue && (
                                <Text
                                  as="span"
                                  fontSize="inherit"
                                  fontWeight="medium"
                                  color="errorFillInverse"
                                >
                                  Overdue: {extension.overdue} day
                                  {extension.overdue > 1 ? 's' : ''}
                                </Text>
                              )}
                            </HStack>
                          </ListItem>
                        ))}

                        {groupIndex <
                          (reviewTimelineData?.deadlineHistory?.length ?? 0) -
                            1 && (
                          <>
                            {reviewTimelineData?.withdrawalPeriods?.[
                              groupIndex
                            ] && (
                              <Text
                                mt={4}
                                mb={2}
                                fontWeight="bold"
                                textStyle="textContext"
                              >
                                Reviewer Withdrawn:{' '}
                                {reviewTimelineData.withdrawalPeriods[
                                  groupIndex
                                ].withdrawnDate
                                  ? formattedDate(
                                      new Date(
                                        reviewTimelineData.withdrawalPeriods[
                                          groupIndex
                                        ].withdrawnDate
                                      )
                                    )
                                  : '-'}{' '}
                                | Reviewer Reinvited:{' '}
                                {reviewTimelineData.withdrawalPeriods[
                                  groupIndex
                                ].reinvitedDate
                                  ? formattedDate(
                                      new Date(
                                        reviewTimelineData.withdrawalPeriods[
                                          groupIndex
                                        ].reinvitedDate
                                      )
                                    )
                                  : '-'}
                              </Text>
                            )}

                            {reviewTimelineData?.rescindedPeriods?.[
                              groupIndex
                            ] && (
                              <Text
                                mt={4}
                                mb={2}
                                fontWeight="bold"
                                textStyle="textContext"
                              >
                                Review Rescinded:{' '}
                                {reviewTimelineData.rescindedPeriods[groupIndex]
                                  .rescindedDate
                                  ? formattedDate(
                                      new Date(
                                        reviewTimelineData.rescindedPeriods[
                                          groupIndex
                                        ].rescindedDate
                                      )
                                    )
                                  : '-'}
                              </Text>
                            )}
                          </>
                        )}
                      </>
                    </Box>
                  )
                )}
              </UnorderedList>
            )}
        </Stack>
      </Box>
    </Collapse>
  );
}
